import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Galleria } from 'primereact/galleria';
import { Image } from 'primereact/image';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Fieldset } from 'primereact/fieldset';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useTheme } from '../hooks/useTheme';
import { catalogService } from '../services';
import { buildImageUrl } from '../lib/fileServer';
import { formatoImporte } from '../lib/formatos';
import toast from 'react-hot-toast';

// Components
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import AddToCartButton from '../components/landing/AddToCartButton';
import GcrRatingBadge from '../components/landing/GcrRatingBadge';

const ProductPage = () => {
    const { id } = useParams();
    const { theme, toggleTheme } = useTheme();
    const [producto, setProducto] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [liked, setLiked] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const lastTrackedIdRef = useRef(null);
    // Nuevo estado para la cantidad en el flujo de WhatsApp
    const [cantidadCompra, setCantidadCompra] = useState(1);

    // Función para calcular precios con promociones aplicadas
    const calcularPreciosConPromociones = useCallback((comercial, promociones) => {
        if (!promociones || promociones.length === 0) {
            return {
                precio_lista: comercial?.precio_venta || 0,
                precio_venta: comercial?.precio_venta || 0,
                promocion_aplicada: null,
                tiene_promocion: false
            };
        }

        // Buscar la promoción más vigente que se pueda aplicar (cantidad_requerida = 1)
        const promocionAplicable = promociones.find(promo =>
            promo.cantidad_requerida <= 1 &&
            promo.id_tipo_promocion === 3 // Tipo que sustituye precio unitario
        );

        if (promocionAplicable) {
            return {
                precio_lista: promocionAplicable.precio_original,
                precio_venta: promocionAplicable.precio_promocional,
                promocion_aplicada: promocionAplicable,
                tiene_promocion: true,
                ahorro_monto: promocionAplicable.ahorro_monto,
                ahorro_porcentaje: promocionAplicable.ahorro_porcentaje
            };
        }

        return {
            precio_lista: comercial?.precio_venta || 0,
            precio_venta: comercial?.precio_venta || 0,
            promocion_aplicada: null,
            tiene_promocion: false
        };
    }, []);

    // Configuración responsiva para Galleria
    const responsiveOptions = [
        {
            breakpoint: '1024px',
            numVisible: 5
        },
        {
            breakpoint: '960px',
            numVisible: 4
        },
        {
            breakpoint: '768px',
            numVisible: 3
        },
        {
            breakpoint: '560px',
            numVisible: 1
        }
    ];

    // Funciones del modal de imágenes (deben estar antes de los templates)
    const openImageModal = (index) => {
        // Asegurar que el índice sea válido
        const validIndex = Math.max(0, Math.min(index || 0, (galleryImages?.length || 1) - 1));
        setCurrentImageIndex(validIndex);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
    };

    const nextImage = () => {
        if (!galleryImages || galleryImages.length === 0) return;
        setCurrentImageIndex((prevIndex) =>
            prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        if (!galleryImages || galleryImages.length === 0) return;
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
        );
    };

    // Utilidad para limpiar HTML en descripciones antes de usar en metatags
    const stripHtml = (html) => {
        if (typeof html !== 'string') return '';
        return html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    };

    // Templates para Galleria (deben estar antes de los returns condicionales)
    const itemTemplate = useCallback((item, index) => {
        return (
            <div className="relative cursor-pointer" style={{ width: '100%', height: '400px' }} onClick={() => openImageModal(index)}>
                <img src={item.itemImageSrc} alt={item.alt} style={{ width: '100%', height: '100%', objectFit: 'contain' }} className="border-round hover:opacity-90 transition-opacity" />
                <div className="absolute top-2 right-2 bg-black-alpha-50 border-round p-2">
                    <i className="pi pi-search-plus text-white"></i>
                </div>
            </div>
        );
    }, [openImageModal]);

    const thumbnailTemplate = useCallback((item) => {
        return (
            <div className="relative cursor-pointer border-2 border-transparent hover:border-primary transition-colors duration-200" style={{ width: '80px', height: '80px' }}>
                <img src={item.thumbnailImageSrc} alt={item.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} className="border-round" />
            </div>
        );
    }, []);



    // Función para renderizar especificaciones de forma jerárquica
    const renderizarEspecificaciones = (especificaciones) => {
        if (!especificaciones) return null;

        const plantillaId = especificaciones?.id_plantilla ?? especificaciones?.metadata?.id_plantilla ?? null;

        const hasValue = (v) => {
            if (v === null || v === undefined) return false;
            if (typeof v === 'string') {
                const s = v.trim();
                if (s === '' || s.toLowerCase() === 'null') return false;
            }
            return true;
        };

        const renderValue = (v) => {
            const value = v ?? '';
            if (typeof value === 'string' && /<[^>]+>/.test(value)) {
                return <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: value }} />;
            }
            return <span className="text-primary">{value}</span>;
        };

        // Plantillas distintas de 1: mostrar solo el valor dentro de especificaciones
        if (plantillaId !== 1) {
            if (typeof especificaciones === 'string') {
                return <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: especificaciones }} />;
            }
            const valuesArray = especificaciones?.parametros ? Object.values(especificaciones.parametros) : [];
            const ordered = valuesArray.sort((a, b) => (a?.orden ?? 0) - (b?.orden ?? 0));
            if (ordered.length === 0) return null;

            return (
                <div className="specifications-container">
                    <table className="w-full text-lg">
                        <tbody>
                            {ordered.map((p) => {
                                const v = p?.valor_display ?? p?.valor;
                                if (!hasValue(v)) return null;
                                return (
                                    <tr key={p.parametro_id} className="border-bottom-1 surface-border">
                                        <td className="p-3" colSpan={3}>{renderValue(v)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Plantilla 1: render estructurado
        if (!especificaciones?.parametros) return null;
        const paramsArray = Object.values(especificaciones.parametros);

        const grupos = paramsArray
            .filter(p => (p.nivel || 0) === 0)
            .sort((a, b) => (a?.orden ?? 0) - (b?.orden ?? 0));

        return (
            <div className="specifications-container">
                <table className="w-full border-collapse">
                    <tbody>
                        {grupos.map((param) => {
                            const paramValue = param.valor_display ?? param.valor;
                            const rows = [];

                            // Fila principal: sin etiqueta, solo el valor si existe
                            if (hasValue(paramValue)) {
                                rows.push({ label: '', value: paramValue, key: `${param.parametro_id}-primary` });
                            }

                            // Sub-parámetros válidos
                            const subParams = paramsArray
                                .filter(sp => sp.id_padre === param.parametro_id && (sp.nivel || 0) === 1)
                                .sort((a, b) => (a?.orden ?? 0) - (b?.orden ?? 0));

                            subParams.forEach(sp => {
                                const val = sp.valor_display ?? sp.valor;
                                if (!hasValue(val)) return;
                                const label = sp.parametro_nombre === 'Comentario' ? '' : sp.parametro_nombre;
                                rows.push({ label, value: val, key: `${sp.parametro_id}` });
                            });

                            if (rows.length === 0) return null;

                            return (
                                <React.Fragment key={param.parametro_id}>
                                    {rows.map((row, index) => (
                                        <tr key={row.key} className="border-bottom-1 surface-border hover:bg-secondary transition-colors">
                                            {index === 0 && (
                                                <td
                                                    rowSpan={rows.length}
                                                    className="p-3 font-semibold text-primary border-right-1 surface-border bg-primary align-top"
                                                    style={{ width: '30%' }}
                                                >
                                                    {param.parametro_nombre}
                                                </td>
                                            )}
                                            {row.label ? (
                                                <>
                                                    <td className="p-2 font-medium text-secondary" style={{ width: '25%' }}>
                                                        {row.label}
                                                    </td>
                                                    <td className="p-2" style={{ width: '45%' }}>
                                                        {renderValue(row.value)}
                                                    </td>
                                                </>
                                            ) : (
                                                <td className="p-2" style={{ width: '70%' }} colSpan={2}>
                                                    {renderValue(row.value)}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    useEffect(() => {
        const fetchProducto = async () => {
            try {
                setLoading(true);
                const productData = await catalogService.getProductById(id);
                setProducto(productData);
            } catch (err) {
                console.error('Error cargando producto:', err);
                setError('Error de conexión al cargar el producto');
                toast.error('Error al cargar el producto');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProducto();
        }
    }, [id]);

    // Tracking de vista de producto (GA4 view_item) - debe declararse antes de cualquier return condicional
    useEffect(() => {
        if (!producto) return;
        if (lastTrackedIdRef.current === id) return;
        try {
            const base = producto?.data || producto || {};
            const comercialLocal = base.comercial || {};
            const promocionesLocal = base.promociones || [];
            const preciosLocal = calcularPreciosConPromociones(comercialLocal, promocionesLocal);
            const item = {
                item_id: comercialLocal?.sku || id,
                item_name: comercialLocal?.nombre || `Producto ${id}`,
                item_brand: comercialLocal?.marca || undefined,
                price: Number(preciosLocal?.precio_venta || 0)
            };
            const value = Number(preciosLocal?.precio_venta || 0);
            if (typeof window !== 'undefined' && window.dataLayer) {
                window.dataLayer.push({ event: 'view_item', currency: 'PEN', value, items: [item] });
                lastTrackedIdRef.current = id;
            }
        } catch (e) {
        }
    }, [producto, id, calcularPreciosConPromociones]);

    if (loading) {
        return (
            <div className={`landing-page theme-${theme}`}>
                <Header theme={theme} onToggleTheme={toggleTheme} />
                <main className="landing-main">
                    <div className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center">
                        <ProgressSpinner />
                        <p className="mt-3">Cargando producto...</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`landing-page theme-${theme}`}>
                <Helmet>
                    <title>Error - WINS Technology</title>
                    <meta name="description" content="Error al cargar el producto." />
                </Helmet>
                <Header theme={theme} onToggleTheme={toggleTheme} />
                <main className="landing-main">
                    <div className="surface-section px-4 py-8 md:px-6 lg:px-8">
                        <Message severity="error" text={error} className="w-full" />
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (!producto) {
        return (
            <div className={`landing-page theme-${theme}`}>
                <Helmet>
                    <title>Producto no encontrado - WINS Technology</title>
                    <meta name="description" content="El producto que buscas no fue encontrado." />
                </Helmet>
                <Header theme={theme} onToggleTheme={toggleTheme} />
                <main className="landing-main">
                    <div className="surface-section px-4 py-8 md:px-6 lg:px-8">
                        <Message severity="warn" text="Producto no encontrado" className="w-full" />
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Destructuring de datos del producto según datos-estructura.json
    const {
        comercial = {},
        descripcion = {},
        especificaciones = {},
        stocks = [],
        fotos = [],
        promociones = []
    } = producto?.data || producto || {};

    const preciosCalculados = calcularPreciosConPromociones(comercial, promociones);

    // Preparar imágenes para Galleria - ordenar para que la predeterminada sea primera
    const galleryImages = fotos && fotos.length > 0
        ? fotos
            .sort((a, b) => (b.predeterminado ? 1 : 0) - (a.predeterminado ? 1 : 0)) // Foto predeterminada primero
            .map((foto, index) => {
                // Manejar diferentes estructuras posibles de foto
                const imageUrl = buildImageUrl(foto.url || foto.imagen || foto);
                return {
                    itemImageSrc: imageUrl,
                    thumbnailImageSrc: imageUrl,
                    alt: foto.descripcion || `${comercial?.nombre || 'Producto'} - Imagen ${index + 1}`,
                    title: foto.descripcion || `Imagen ${index + 1}`,
                    predeterminado: foto.predeterminado || false
                };
            })
        : [{
            itemImageSrc: '/productos/placeholder.png',
            thumbnailImageSrc: '/productos/placeholder.png',
            alt: 'Imagen no disponible',
            title: 'Imagen no disponible'
        }];

    // Variables SEO
    const siteUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const productUrl = `${siteUrl}/producto/${id}`;
    const primaryImageUrl = (galleryImages && galleryImages.length > 0)
        ? galleryImages[0].itemImageSrc
        : `${siteUrl}/productos/placeholder.png`;
    const descriptionText = stripHtml(
        descripcion?.descripcion_principal || descripcion?.descripcion_canal || 'Producto disponible en Laptops.pe'
    );
    const inStock = (stocks && stocks.some(s => (s?.stock_disponible || 0) > 0));
    const availabilitySchema = inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
    // Código del fabricante (MPN) para SEO
    const manufacturerCode = especificaciones?.metadata?.codigofabricante || null;



    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>{comercial?.nombre ? `${comercial.nombre} - Laptops.pe` : 'Producto - Laptops.pe'}</title>
                <meta name="description" content={descriptionText} />

                {/* Open Graph básico y de producto */}
                <meta property="og:title" content={comercial?.nombre || 'Producto'} />
                <meta property="og:description" content={descriptionText} />
                <meta property="og:type" content="product" />
                <meta property="og:site_name" content="Laptops.pe" />
                <meta property="og:locale" content="es_PE" />
                <meta property="og:url" content={productUrl} />
                <meta property="og:image" content={primaryImageUrl} />

                {/* Extensiones OG de producto */}
                <meta property="product:availability" content={inStock ? 'in stock' : 'out of stock'} />
                <meta property="product:price:amount" content={(preciosCalculados?.precio_venta || 0).toFixed(2)} />
                <meta property="product:price:currency" content="PEN" />
                {manufacturerCode && (
                    <meta property="product:retailer_item_id" content={manufacturerCode} />
                )}

                {/* Twitter Cards */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={comercial?.nombre || 'Producto'} />
                <meta name="twitter:description" content={descriptionText} />
                <meta name="twitter:image" content={primaryImageUrl} />
                <meta name="twitter:image:alt" content={comercial?.nombre || 'Producto'} />
                {manufacturerCode && (
                    <>
                        <meta name="twitter:label1" content="Código fabricante" />
                        <meta name="twitter:data1" content={manufacturerCode} />
                    </>
                )}

                {/* Canonical y ayudas para algunos scrapers */}
                <link rel="canonical" href={productUrl} />
                <link rel="image_src" href={primaryImageUrl} />

                {/* Structured Data para SEO */}
                <script type="application/ld+json">
                    {JSON.stringify((() => {
                        const schema = {
                            "@context": "https://schema.org",
                            "@type": "Product",
                            "name": comercial?.nombre || 'Producto',
                            "description": descriptionText,
                            "image": primaryImageUrl,
                            "url": productUrl,
                            "offers": {
                                "@type": "Offer",
                                "url": productUrl,
                                "price": Number(preciosCalculados?.precio_venta || 0).toFixed(2),
                                "priceCurrency": "PEN",
                                "availability": availabilitySchema,
                                "itemCondition": "https://schema.org/NewCondition"
                            }
                        };
                        if (comercial?.marca) {
                            schema.brand = { "@type": "Brand", "name": comercial.marca };
                        }
                        if (comercial?.sku) {
                            schema.sku = comercial.sku;
                        }
                        if (manufacturerCode) {
                            // MPN (Manufacturer Part Number): recomendado por schema.org
                            schema.mpn = manufacturerCode;
                        }
                        return schema;
                    })())}
                </script>
            </Helmet>

            <Tooltip target=".manufacturer-code-tooltip" position="top" />

            <Header theme={theme} onToggleTheme={toggleTheme} />

            <main className="landing-main">
                <div className="surface-section px-0 pb-6 md:px-6 lg:px-2">
                    {/* Breadcrumb */}
                    <ol className="hidden md:flex list-none m-0 p-0 align-items-center text-sm text-600 mb-5 border-top-1 border-bottom-1 surface-border py-2 overflow-x-auto">
                        <li className="mr-2">Inicio</li>
                        <li className="mr-2 inline-flex align-items-center"><i className="pi pi-angle-right text-sm"></i></li>
                        <li className="mr-2">Productos</li>
                        <li className="mr-2 inline-flex align-items-center"><i className="pi pi-angle-right text-sm"></i></li>
                        <li>{comercial?.nombre || 'Producto'}</li>
                    </ol>

                    <div className="grid">
                        {/* Galería de imágenes */}
                        <div className="col-12 lg:col-6">
                            <Galleria value={galleryImages} responsiveOptions={responsiveOptions} numVisible={4} circular style={{ maxWidth: '100%' }} item={itemTemplate} thumbnail={thumbnailTemplate} showThumbnails showItemNavigators showIndicators />

                            {/* Sección de Confianza y Envíos */}
                            <div className="mt-4 p-3 surface-card border-round shadow-1">
                                <div className="flex align-items-center mb-3 text-600 text-sm italic">
                                    <i className="pi pi-info-circle mr-2"></i>
                                    <span>Las imágenes mostradas son referenciales.</span>
                                </div>

                                <div className="grid">
                                    <div className="col-12 md:col-6 mb-2">
                                        <div className="flex align-items-start">
                                            <i className="pi pi-truck text-primary mr-3 text-xl"></i>
                                            <div>
                                                <span className="block font-bold text-900 border-bottom-1 surface-border mb-1">Envíos Seguros</span>
                                                <span className="text-700 text-sm">• Lima Metropolitana: Directo a tu domicilio.</span>
                                                <span className="block text-700 text-sm">• Nivel Nacional: Vía Shalom u Olva Courier.</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6 mb-2">
                                        <div className="flex align-items-start">
                                            <i className="pi pi-calendar-clock text-orange-500 mr-3 text-xl"></i>
                                            <div>
                                                <span className="block font-bold text-900 border-bottom-1 surface-border mb-1">Atención Exclusiva</span>
                                                <p className="m-0 text-700 text-sm">Por campaña, las atenciones son <strong>solo en línea</strong> para evitar demoras y brindarte la mejor atención personalizada.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 p-3 bg-primary-reverse border-round flex align-items-center justify-content-between">
                                    <div className="flex align-items-center">
                                        <i className="pi pi-video mr-3 text-2xl"></i>
                                        <div>
                                            <span className="block font-bold">¿Deseas mayor seguridad?</span>
                                            <span className="text-sm">Mira nuestras evidencias y envíos diarios.</span>
                                        </div>
                                    </div>
                                    <Button
                                        label="Ver en TikTok"
                                        icon="pi pi-external-link"
                                        className="p-button-sm p-button-outlined p-button-secondary"
                                        onClick={() => window.open('https://www.tiktok.com/@laptops_pe', '_blank')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Información del producto */}
                        <div className="col-12 lg:col-6 py-3 lg:pl-6">
                            <div className="text-xl md:text-2xl font-bold text-900 mb-2">
                                {comercial?.nombre || 'Producto sin nombre'}
                            </div>
                            <div className="flex align-items-center mb-4">
                                <i className={classNames('pi cursor-pointer', { 'pi-heart text-600': !liked, 'pi-heart-fill text-orange-500': liked })} onClick={() => setLiked(prevState => !prevState)}></i>
                            </div>
                            {/*<div className="mb-3">
                                <span className="text-700 mb-2 block">Opiniones del comercio verificadas por Google</span>
                                <GcrRatingBadge />
                            </div>*/}

                            {/* Sección de precios y promociones */}
                            <div className="mb-5">
                                {preciosCalculados.tiene_promocion ? (
                                    <div className="bg-primary border-1 border-cyan-500 border-round p-4 mb-4">
                                        <div className="flex align-items-center mb-3">
                                            <i className="pi pi-tag text-orange-500 text-xl mr-2"></i>
                                            <span className="font-bold text-orange-700 text-lg">¡OFERTA ESPECIAL!</span>
                                        </div>
                                        <div className="mb-3">
                                            <span className="text-600 text-sm block mb-1">Precio regular:</span>
                                            <span className="text-600 line-through text-lg mr-3">S/ {formatoImporte(preciosCalculados.precio_lista)}</span>
                                            <span className="bg-red-500 text-white px-2 py-1 border-round text-sm font-bold">
                                                -{preciosCalculados.ahorro_porcentaje}%
                                            </span>
                                        </div>
                                        <div className="mb-3">
                                            <div className="text-2xl font-bold text-primary">
                                                S/ {formatoImporte(preciosCalculados.precio_venta)}
                                            </div>
                                        </div>
                                        <div className="bg-green-100 border-1 border-green-300 border-round p-3">
                                            <div className="flex align-items-center mb-2">
                                                <i className="pi pi-check-circle text-green-600 mr-2"></i>
                                                <span className="font-bold text-green-700">{preciosCalculados.promocion_aplicada.nombre}</span>
                                            </div>
                                            <p className="text-green-600 text-sm mb-2">{preciosCalculados.promocion_aplicada.descripcion}</p>
                                            <div className="flex align-items-center">
                                                <i className="pi pi-money-bill text-green-600 mr-2"></i>
                                                <span className="text-green-700 font-bold">
                                                    ¡Ahorras S/ {preciosCalculados.ahorro_monto.toFixed(2)}!
                                                </span>
                                            </div>
                                            {preciosCalculados.promocion_aplicada.fecha_fin && (
                                                <div className="flex align-items-center mt-2">
                                                    <i className="pi pi-clock text-orange-500 mr-2"></i>
                                                    <span className="text-orange-600 text-sm font-medium">
                                                        Válido hasta: {new Date(preciosCalculados.promocion_aplicada.fecha_fin).toLocaleDateString('es-PE')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-900 font-medium text-3xl block mb-5">
                                        S/. {preciosCalculados.precio_venta.toFixed(2)}
                                    </span>
                                )}

                                {/* Advertencia de recargo por tarjeta */}
                                <div className="bg-yellow-50 border-1 border-yellow-300 border-round p-3 mb-3">
                                    <div className="flex align-items-start">
                                        <i className="pi pi-exclamation-triangle text-yellow-600 mr-2 mt-1"></i>
                                        <div>
                                            <p className="font-medium text-yellow-800 mb-1">
                                                Información importante sobre pagos
                                            </p>
                                            <p className="text-yellow-700 text-sm mb-0">
                                                Los pagos con tarjeta de crédito o débito tienen un recargo del 5%
                                                debido a las políticas de la pasarela de pagos.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Botón de agregar al carrito (COMENTADO SEGÚN REQUERIMIENTO PARA PIVOT A WHATSAPP) */}
                            {/* <div className="">
                                <AddToCartButton
                                    producto={{
                                        id: id,
                                        nombre: comercial?.nombre || 'Producto sin nombre',
                                        precio_venta: preciosCalculados.precio_venta,
                                        imagen_principal: fotos && fotos.length > 0 ? buildImageUrl(fotos[0].url) : '/productos/placeholder.png',
                                        stock_disponible: stocks?.[0]?.stock_disponible || 0,
                                        tiene_promocion: preciosCalculados.tiene_promocion,
                                        precio_original: preciosCalculados.precio_lista
                                    }}
                                    size="large"
                                    className="mb-0"
                                />
                            </div> */}

                            {/* NUEVO: Selector de cantidad y Botón de Compra por WhatsApp */}
                            <div className="flex flex-column gap-3 mb-4">
                                <div className="flex align-items-center gap-3">
                                    <label htmlFor="compra-cantidad" className="font-bold text-900">Cantidad:</label>
                                    <input
                                        id="compra-cantidad"
                                        type="number"
                                        min="1"
                                        max={stocks.reduce((total, s) => total + (s.stock_disponible || 0), 0) || 1}
                                        value={cantidadCompra}
                                        onChange={(e) => setCantidadCompra(Math.max(1, parseInt(e.target.value) || 1))}
                                        className="p-inputtext p-component w-5rem text-center font-bold"
                                        disabled={!stocks.some(s => s.stock_disponible > 0)}
                                    />
                                    <span className="text-600 text-sm">
                                        {stocks.some(s => s.stock_disponible > 0)
                                            ? `(${stocks.reduce((total, s) => total + (s.stock_disponible || 0), 0)} unidades disponibles)`
                                            : '(Agotado)'}
                                    </span>
                                </div>

                                <Button
                                    label={stocks.some(s => s.stock_disponible > 0) ? "Comprar por WhatsApp" : "Agotado"}
                                    icon="pi pi-whatsapp"
                                    className="w-full p-button-lg font-bold text-white shadow-2 transition-all transition-duration-300 hover:scale-102"
                                    style={{
                                        backgroundColor: stocks.some(s => s.stock_disponible > 0) ? '#25D366' : '#888888',
                                        borderColor: stocks.some(s => s.stock_disponible > 0) ? '#25D366' : '#888888'
                                    }}
                                    disabled={!stocks.some(s => s.stock_disponible > 0)}
                                    onClick={() => {
                                        const numeroWhatsApp = '51978326796';
                                        const almacenesDisponibles = stocks
                                            .filter(s => s.stock_disponible > 0)
                                            .map(s => `${s.almacen_nombre} (${s.stock_disponible > 5 ? 'Más de 5' : s.stock_disponible} ud.)`)
                                            .join(', ');

                                        const stockTexto = almacenesDisponibles || 'Consultar disponibilidad';
                                        const precioTexto = `S/ ${preciosCalculados.precio_venta.toFixed(2)}`;
                                        const codigoFab = especificaciones?.metadata?.codigofabricante || 'No especificado';
                                        const urlProducto = window.location.href;

                                        const mensaje =
                                            `¡Hola Laptops.pe! 🖥️ Me interesa realizar la compra de este producto:

🛒 *Producto:* ${comercial?.nombre || 'Producto'}
💵 *Precio:* ${precioTexto}
🏷️ *Cód. Fabricante:* ${codigoFab}
🏢 *Disponible en:* ${stockTexto}
🔢 *Cantidad deseada:* ${cantidadCompra}
🔗 *Enlace:* ${urlProducto}

¿Me podrían ayudar a concretar el pedido y coordinar el pago? ¡Muchas gracias!`;

                                        window.open(`https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener,noreferrer');
                                    }}
                                />
                            </div>

                            {/* Grid de información de garantía/stock y tabla de almacenes */}
                            <div className="grid">
                                {/* Columna 1: Información de garantía y disponibilidad */}
                                <div className="col-12 md:col-4">
                                    {/* Información de garantía */}
                                    {comercial?.tipo_garantia && (
                                        <div className="mb-4">
                                            <div className="font-bold text-900 mb-2">Garantía</div>
                                            <div className="flex align-items-center">
                                                <i className="pi pi-shield text-green-500 mr-2"></i>
                                                <span className="text-700">{comercial.tipo_garantia}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Información de stock general */}
                                    {stocks && stocks.length > 0 && (
                                        <div className="mb-4">
                                            <div className="font-bold text-900 mb-2">Disponibilidad General</div>
                                            <div className="flex align-items-center">
                                                {stocks.some(stock => stock.stock_disponible > 0) ? (
                                                    <>
                                                        <i className="pi pi-check-circle text-green-500 mr-2"></i>
                                                        <span className="text-green-600 font-medium">Disponible en almacenes</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="pi pi-times-circle text-red-500 mr-2"></i>
                                                        <span className="text-red-600 font-medium">Sin stock</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="mb-4">
                                        <div className="font-bold text-900 mb-2">Código de fabricante</div>
                                        <div className="flex align-items-center">
                                            <i className="pi pi-barcode text-blue-500 mr-2"></i>
                                            <span
                                                className="text-700 manufacturer-code-tooltip cursor-help border-bottom-1 border-dashed surface-border font-medium"
                                                data-pr-tooltip="Utilice este código oficial para validar las especificaciones técnicas completas y la autenticidad del producto en la web oficial del fabricante."
                                            >
                                                {especificaciones.metadata.codigofabricante}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Tabla de stocks por almacén */}
                                <div className="col-12 md:col-8">
                                    {stocks && stocks.length > 0 && (
                                        <div className="mb-4">
                                            <div className="font-bold text-900 mb-2">Stock por Almacén</div>
                                            <div className="border-1 surface-border border-round overflow-hidden">
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="bg-primary">
                                                            <th className="p-2 text-left font-semibold text-primary border-bottom-1 surface-border">Almacén</th>
                                                            <th className="p-2 text-center font-semibold text-primary border-bottom-1 surface-border">Stock</th>
                                                            <th className="p-2 text-center font-semibold text-primary border-bottom-1 surface-border">Reservado</th>
                                                            <th className="p-2 text-center font-semibold text-primary border-bottom-1 surface-border">Disponible</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {stocks.map((stock, index) => (
                                                            <tr key={index} className="hover:surface-hover transition-colors">
                                                                <td className="p-2 border-bottom-1 surface-border font-medium">{stock.almacen_nombre}</td>
                                                                <td className="p-2 text-center border-bottom-1 surface-border">
                                                                    {stock.stock_total > 5 ? 'Más de 5' : stock.stock_total}
                                                                </td>
                                                                <td className="p-2 text-center border-bottom-1 surface-border">{stock.stock_reservado}</td>
                                                                <td className="p-2 text-center border-bottom-1 surface-border">
                                                                    <span className={stock.stock_disponible > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                                                        {stock.stock_disponible > 5 ? 'Más de 5' : stock.stock_disponible}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs con información detallada */}
                    <div className="mt-6">
                        <TabView>
                            {/* Tab de Descripción */}
                            <TabPanel header="Descripción" leftIcon="pi pi-info-circle mr-2">
                                <div className="py-4">
                                    <div
                                        className="prose max-w-none"
                                        style={{
                                            maxWidth: '100%',
                                            overflow: 'hidden'
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: descripcion?.descripcion_principal || descripcion?.descripcion_canal || 'No hay descripción disponible para este producto.'
                                        }}
                                    />
                                </div>
                            </TabPanel>

                            {/* Tab de Especificaciones */}
                            <TabPanel header="Especificaciones" leftIcon="pi pi-cog mr-2">
                                <div className="py-4">
                                    {especificaciones && especificaciones.parametros ? (
                                        renderizarEspecificaciones(especificaciones)
                                    ) : (
                                        <p className="text-secondary">No hay especificaciones técnicas disponibles para este producto.</p>
                                    )}
                                </div>
                            </TabPanel>

                            {/* Tab de Garantía y Soporte */}
                            <TabPanel header="Garantía y Soporte" leftIcon="pi pi-shield mr-2">
                                <div className="py-4">
                                    <div className="grid">
                                        <div className="col-12 md:col-4">
                                            <Fieldset legend="Garantía" className="mb-4">
                                                <div className="flex align-items-center mb-3">
                                                    <i className="pi pi-shield text-green-500 text-2xl mr-3"></i>
                                                    <div>
                                                        <div className="font-semibold text-900">
                                                            {comercial?.vigencia_garantia ? `${comercial.vigencia_garantia} meses` : 'Consultar garantía'}
                                                        </div>
                                                        <div className="text-600 text-sm">
                                                            {comercial?.tipo_garantia || 'Garantía estándar'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-700 text-sm line-height-3">
                                                    {comercial?.tipo_garantia?.toLowerCase().includes('casa') ? (
                                                        <div>
                                                            <p className="mb-2">
                                                                <strong>Garantía cubierta por el canal de ventas.</strong>
                                                                Nuestro equipo se encarga de gestionar cualquier inconveniente
                                                                que puedas tener con tu producto.
                                                            </p>
                                                            <p className="mb-0">
                                                                Contamos con soporte técnico especializado para brindarte
                                                                la mejor atención y resolver tus consultas de manera eficiente.
                                                            </p>
                                                        </div>
                                                    ) : comercial?.tipo_garantia?.toLowerCase().includes('marca') ? (
                                                        <div>
                                                            <p className="mb-2">
                                                                <strong>Garantía oficial del fabricante.</strong>
                                                                Válida en cualquier punto del país donde se encuentre
                                                                su representante autorizado.
                                                            </p>
                                                            <p className="mb-0">
                                                                Puedes acudir directamente a los centros de servicio
                                                                autorizados de la marca para hacer efectiva tu garantía.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <p>
                                                            Nuestros productos cuentan con garantía completa.
                                                            Soporte técnico especializado disponible para resolver
                                                            cualquier inconveniente.
                                                        </p>
                                                    )}
                                                </div>
                                            </Fieldset>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <Fieldset legend="Entrega" className="mb-4">
                                                <div className="flex align-items-center mb-3">
                                                    <i className="pi pi-truck text-blue-500 text-2xl mr-3"></i>
                                                    <div>
                                                        <div className="font-semibold text-900">Envío seguro</div>
                                                        <div className="text-600 text-sm">Lima y provincias</div>
                                                    </div>
                                                </div>
                                                <div className="text-700 text-sm line-height-3">
                                                    <p className="mb-2">
                                                        <strong>Lima Metropolitana:</strong> Entrega a domicilio
                                                        con seguimiento en tiempo real.
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Provincias:</strong> Envío a la agencia que nos
                                                        informes al momento de coordinar tu compra.
                                                    </p>
                                                    <div className="bg-blue-50 border-1 border-blue-200 border-round p-2 mt-2">
                                                        <p className="mb-1 font-medium text-blue-800">
                                                            <i className="pi pi-info-circle mr-1"></i>
                                                            Importante:
                                                        </p>
                                                        <p className="mb-1 text-blue-700 text-xs">
                                                            • El plazo de envío se cuenta desde la confirmación de pago
                                                            por parte del Dpto. de Contabilidad.
                                                        </p>
                                                        <p className="mb-0 text-blue-700 text-xs">
                                                            • Pagos con tarjeta: confirmación puede tardar más por la pasarela.
                                                            • Transferencias bancarias: confirmación casi inmediata.
                                                        </p>
                                                    </div>
                                                </div>
                                            </Fieldset>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <Fieldset legend="Soporte" className="mb-4">
                                                <div className="flex align-items-center mb-3">
                                                    <i className="pi pi-headphones text-orange-500 text-2xl mr-3"></i>
                                                    <div>
                                                        <div className="font-semibold text-900">Atención especializada</div>
                                                        <div className="text-600 text-sm">Canal de ventas y técnico</div>
                                                    </div>
                                                </div>
                                                <div className="text-700 text-sm line-height-3">
                                                    <p className="mb-2">
                                                        <strong>Canal de ventas 24/7:</strong> Nuestros ejecutivos
                                                        están disponibles para atenderte. Las respuestas pueden
                                                        tardar si todos están ocupados.
                                                    </p>
                                                    <div className="bg-orange-50 border-1 border-orange-200 border-round p-2">
                                                        <p className="mb-1 font-medium text-orange-800">
                                                            <i className="pi pi-clock mr-1"></i>
                                                            Soporte técnico:
                                                        </p>
                                                        <p className="mb-1 text-orange-700 text-xs">
                                                            • Lunes a viernes: 9:00 AM - 5:00 PM
                                                        </p>
                                                        <p className="mb-0 text-orange-700 text-xs">
                                                            • Sábados: 9:00 AM - 1:00 PM
                                                        </p>
                                                    </div>
                                                </div>
                                            </Fieldset>
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                        </TabView>
                    </div>
                </div>
            </main>

            <Footer />

            {/* Modal de galería de imágenes */}
            <Dialog visible={showImageModal} onHide={closeImageModal} header="Galería de Imágenes" modalClassName="p-fluid" style={{ width: '95vw', height: '95vh' }} maximizable>
                <div className="flex flex-column align-items-center justify-content-center h-full">
                    {galleryImages && galleryImages.length > 0 && (
                        <>
                            <div className="relative flex align-items-center justify-content-center" style={{ width: '100%', height: '70vh' }}>
                                {/* Botón anterior */}
                                <Button icon="pi pi-chevron-left" className="p-button-rounded p-button-text absolute left-0 z-2" style={{ fontSize: '2rem' }} onClick={prevImage} />

                                {/* Imagen principal */}
                                <Image src={galleryImages[currentImageIndex || 0]?.itemImageSrc} alt={galleryImages[currentImageIndex || 0]?.alt} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />

                                {/* Botón siguiente */}
                                <Button icon="pi pi-chevron-right" className="p-button-rounded p-button-text absolute right-0 z-2" style={{ fontSize: '2rem' }} onClick={nextImage} />
                            </div>

                            {/* Indicadores de imagen */}
                            <div className="flex align-items-center justify-content-center mt-3">
                                <span className="text-600 mr-2">{(currentImageIndex || 0) + 1} de {galleryImages?.length || 0}</span>
                                <div className="flex">
                                    {galleryImages.map((_, index) => (
                                        <div key={index} className={`w-2rem h-1rem mx-1 border-round cursor-pointer transition-colors ${index === (currentImageIndex || 0) ? 'bg-primary' : 'bg-300'}`} onClick={() => setCurrentImageIndex(index)}></div>
                                    ))}
                                </div>
                            </div>

                            {/* Miniaturas */}
                            <div className="flex align-items-center justify-content-center mt-3 overflow-x-auto" style={{ maxWidth: '100%' }}>
                                {galleryImages.map((image, index) => (
                                    <div key={index} className={`cursor-pointer mx-1 border-2 border-round transition-colors ${index === (currentImageIndex || 0) ? 'border-primary' : 'border-transparent'}`} onClick={() => setCurrentImageIndex(index)}>
                                        <img src={image.thumbnailImageSrc} alt={image.alt} style={{ width: '60px', height: '60px', objectFit: 'cover' }} className="border-round" />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default ProductPage;
